import { FC, PropsWithChildren } from 'react'
import { Link } from 'react-router-dom'
import { useWindowSize } from '../../hooks/useWindowSize'
import { styled } from '../../lib/stitches'
import Gradient from '../platform/Gradient'
import { ArpeggiLogo } from '../platform/icons'
import Button from './Button/Button'
import { Column } from './Components'
import { IconProps } from './icons'
import Text from './Text/Text'

const ArpeggiText = styled('div', {
  display: 'flex',
  alignItems: 'center',
  fontSize: '18px',
  lineHeight: '24px',
  fontWeight: '$regular',
  fontFamily: '$poppins',
  color: 'black',
  position: 'fixed',
  whiteSpace: 'nowrap',
  top: '40px',
  left: '40px'
})

const Logo = styled(ArpeggiLogo, {
  marginRight: '10px',
  mixBlendMode: 'multiply'
})

const DesktopIcon: FC<IconProps> = (props) => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M23 29.3599H9.00001V28.6399H15.64V24.3599H3.00001C1.69901 24.3599 0.640015 23.3009 0.640015 21.9999V4.99989C0.640015 3.69889 1.69901 2.63989 3.00001 2.63989H29C30.302 2.63989 31.36 3.69889 31.36 4.99989V21.9999C31.36 23.3019 30.301 24.3599 29 24.3599H16.36V28.6389H23V29.3599ZM1.36001 19.3599V21.9999C1.36001 22.9039 2.09601 23.6399 3.00001 23.6399H29C29.904 23.6399 30.64 22.9049 30.64 21.9999V19.3599H1.36001ZM1.36001 18.6399H30.64V4.99989C30.64 4.09589 29.905 3.35989 29 3.35989H3.00001C2.09601 3.35989 1.36001 4.09589 1.36001 4.99989V18.6399Z"
      fill="currentColor"
    />
  </svg>
)

const BulldoserIcon: FC<IconProps> = (props) => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M17 7.35989C15.382 7.35989 15.14 6.35089 15.14 5.74989V3.99989C15.14 3.85789 15.112 2.61789 14.13 2.61789C12.86 2.61789 12.14 2.02789 12.14 0.999893C12.14 0.800893 12.301 0.639893 12.5 0.639893C12.699 0.639893 12.86 0.800893 12.86 0.999893C12.86 1.22189 12.86 1.89789 14.117 1.89789C15.319 1.89789 15.86 2.98689 15.86 3.99989V5.74989C15.86 6.21189 15.998 6.63989 17 6.63989C17.199 6.63989 17.36 6.80089 17.36 6.99989C17.36 7.19889 17.199 7.35989 17 7.35989ZM14 6.60389C12.908 6.60389 12.146 5.84089 12.146 4.74989V3.99989C12.146 3.80089 12.307 3.63989 12.506 3.63989C12.705 3.63989 12.866 3.80089 12.866 3.99989V4.74989C12.866 5.29789 13.164 5.88489 14.001 5.88489C14.2 5.88489 14.361 6.04589 14.361 6.24489C14.361 6.44389 14.198 6.60389 14 6.60389ZM17 4.36189C16.801 4.36189 16.64 4.20089 16.64 4.00189V2.50789C16.64 1.97889 16.341 1.35989 15.5 1.35989C15.301 1.35989 15.14 1.19889 15.14 0.999893C15.14 0.800893 15.301 0.639893 15.5 0.639893C16.595 0.639893 17.36 1.40789 17.36 2.50789V4.00289C17.36 4.20089 17.199 4.36189 17 4.36189ZM5.00001 27.9999C5.00001 28.5519 4.55201 28.9999 4.00001 28.9999C3.44801 28.9999 3.00001 28.5519 3.00001 27.9999C3.00001 27.4479 3.44801 26.9999 4.00001 26.9999C4.55201 26.9999 5.00001 27.4479 5.00001 27.9999ZM8.50001 26.9999C7.94801 26.9999 7.50001 27.4479 7.50001 27.9999C7.50001 28.5519 7.94801 28.9999 8.50001 28.9999C9.05201 28.9999 9.50001 28.5519 9.50001 27.9999C9.50001 27.4479 9.05201 26.9999 8.50001 26.9999ZM13 26.9999C12.448 26.9999 12 27.4479 12 27.9999C12 28.5519 12.448 28.9999 13 28.9999C13.552 28.9999 14 28.5519 14 27.9999C14 27.4479 13.552 26.9999 13 26.9999ZM17.5 26.9999C16.948 26.9999 16.5 27.4479 16.5 27.9999C16.5 28.5519 16.948 28.9999 17.5 28.9999C18.052 28.9999 18.5 28.5519 18.5 27.9999C18.5 27.4479 18.052 26.9999 17.5 26.9999ZM22 26.9999C21.448 26.9999 21 27.4479 21 27.9999C21 28.5519 21.448 28.9999 22 28.9999C22.552 28.9999 23 28.5519 23 27.9999C23 27.4479 22.552 26.9999 22 26.9999ZM22 31.3599H4.00001C2.14701 31.3599 0.640015 29.8519 0.640015 27.9999C0.640015 26.2689 1.95601 24.8389 3.64001 24.6589V23.3599H1.00001C0.801015 23.3599 0.640015 23.1989 0.640015 22.9999V8.99989C0.640015 8.80089 0.801015 8.63989 1.00001 8.63989H13C13.199 8.63989 13.36 8.80089 13.36 8.99989V10.9999C13.36 11.1989 13.199 11.3599 13 11.3599H11.36V16.6399H19.64V7.36189L18 7.35989V6.63989L20 6.64189C20.199 6.64189 20.36 6.80289 20.36 7.00189V16.6399C21.466 16.6639 22.36 17.5749 22.36 18.6919V20.6389H24.612C24.694 19.6599 25.108 18.7019 25.856 17.9539C25.923 17.8869 26.015 17.8489 26.111 17.8489H28.707C29.145 16.7249 29.824 15.6659 30.745 14.7449L31.255 15.2539C29.687 16.8219 28.823 18.8619 28.823 20.9999C28.823 23.1379 29.686 25.1779 31.255 26.7449L30.745 27.2549C29.824 26.3329 29.144 25.2739 28.707 24.1509H26.111C26.015 24.1509 25.923 24.1129 25.856 24.0459C25.108 23.2979 24.694 22.3409 24.612 21.3609H22.36V24.6599C24.044 24.8399 25.36 26.2689 25.36 28.0009C25.36 29.8529 23.853 31.3599 22 31.3599ZM4.00001 25.3599C2.54401 25.3599 1.36001 26.5439 1.36001 27.9999C1.36001 29.4559 2.54401 30.6399 4.00001 30.6399H22C23.456 30.6399 24.64 29.4559 24.64 27.9999C24.64 26.5439 23.456 25.3599 22 25.3599H4.00001ZM4.36001 24.6399H21.64V18.6929C21.64 17.9589 21.046 17.3609 20.316 17.3609H1.36001V22.6399H4.00001C4.19901 22.6399 4.36001 22.8009 4.36001 22.9999V24.6399ZM26.265 23.4299H28.463C27.999 21.8419 27.999 20.1589 28.463 18.5709H26.265C25.652 19.2349 25.315 20.0919 25.315 21.0009C25.315 21.9099 25.651 22.7659 26.265 23.4299ZM5.36001 16.6399H10.64V11.3599H5.36001V16.6399ZM1.36001 16.6399H4.64001V11.3599H1.36001V16.6399ZM1.36001 10.6399H12.64V9.35989H1.36001V10.6399ZM11 19.3599H9.00001V18.6399H11V19.3599Z"
      fill="currentColor"
    />
  </svg>
)

const Desktop = styled(DesktopIcon, {
  width: '112px',
  height: '112px',
  marginBottom: '16px'
})

const Bulldoser = styled(BulldoserIcon, {
  width: '112px',
  height: '112px',
  marginBottom: '16px'
})

const MobileScreenBlocker: FC<PropsWithChildren<{ type?: 'studio' | 'other' }>> = ({
  children,
  type = 'other'
}) => {
  const { isBreakpoint } = useWindowSize()
  const isMobile = !isBreakpoint('tablet')
  if (!isMobile) return <>{children}</>
  return (
    <Gradient
      options={{ colorPalette: ['#FFFFFF', '#FFEAFE', '#CEE7FE', '#E9F3E0'], scale: 1.5 }}
      css={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        padding: '32px',
        boxSizing: 'border-box',
        color: '$black'
      }}
    >
      <ArpeggiText>
        <Logo />
        Arpeggi Labs
      </ArpeggiText>
      <Column horizontalCenter>
        {type === 'studio' && <Desktop />}
        {type === 'other' && <Bulldoser />}
        <Text
          css={{
            fontSize: '32px',
            fontWeight: '$bold',
            lineHeight: '40px',
            letterSpacing: '-0.03em',
            textAlign: 'center',
            marginBottom: '16px'
          }}
        >
          {type === 'studio' && 'Sign in on Desktop to access the studio'}
          {type === 'other' && 'This page is under construction'}
        </Text>
        <Link to={'/home'}>
          <Button big type={'dark'}>
            Explore Recently minted songs
          </Button>
        </Link>
      </Column>
    </Gradient>
  )
}

export default MobileScreenBlocker
